import styles from "./WaysToStyleCarousel.scss";
import { useRuntimeConstants } from "../../context/runtimeConstants/RuntimeConstantsContext";
import {
  StylingLookProductFromServer,
  StylingLook,
} from "../../types/productTypes/stylingLookProduct";
import { ProductData } from "../../types/productTypes/product";
import { getPrimaryImage, getHeroProduct } from "../../utils/product";
import Image from "../image/Image";
import PlusPill from "../pills/plusPill/PlusPill";
import LookLabel from "./LookLabel";
import { getLookHeroImageUrl } from "../../utils/heroProductUtils";
import classnamesBind from "classnames/bind";

const classnames = classnamesBind.bind(styles);

interface WaysToStyleCarouselProps {
  stylingLooks: StylingLook[];
}

const renderStylingLookHeroImage = (look: StylingLook, index: number) => {
  const heroProduct = getHeroProduct(look.products)[0] ?? look.products[0];

  if (!heroProduct) {
    return null;
  }
  return (
    <Image
      key={`styling-look-hero-${index}`}
      url={getLookHeroImageUrl({
        product: heroProduct,
        looksProducts: look.products,
      })}
      alternateText={heroProduct?.name}
      type="carousel"
      width={640}
      additionalClasses=""
      loadingType="lazy"
      ariaHidden={false}
    />
  );
};

const renderStylingLookThumbnailImage = (
  product: StylingLookProductFromServer,
  index: number
) => (
  <Image
    key={`styling-look-thumbnail-${index}`}
    url={getPrimaryImage(product.images)?.url}
    alternateText={product.name}
    type="thumbnail"
    width={75}
    additionalClasses=""
    loadingType="lazy"
    ariaHidden={false}
  />
);

const getThumbnailsToShow = (
  products: StylingLookProductFromServer[],
  currentProductId?: number
) => {
  const orderedProducts = [...products];

  if (currentProductId !== undefined) {
    const currentIndex = orderedProducts.findIndex(
      (product) => product.id === currentProductId
    );

    if (currentIndex > 0) {
      const [currentProduct] = orderedProducts.splice(currentIndex, 1);
      orderedProducts.unshift(currentProduct);
    }
  }
  return {
    thumbnailsToShow: orderedProducts.slice(0, 4),
    shouldShowPlusPill: orderedProducts.length > 4,
    remainingCount: Math.max(0, orderedProducts.length - 4),
  };
};

export default function WaysToStyleCarousel({
  stylingLooks,
}: WaysToStyleCarouselProps) {
  const { getTranslation, product } = useRuntimeConstants() as {
    getTranslation: (key: string) => string;
    product: ProductData;
  };

  if (stylingLooks.length === 0) {
    return null;
  }

  const isSingleLook = stylingLooks.length === 1;

  return (
    <div
      data-testid="waysToStyleCarousel"
      className={styles.carousel}
      aria-label={getTranslation("pdp_buy_the_look_carousel_accessibility")}
    >
      {stylingLooks.map((look, index) => {
        const { thumbnailsToShow, shouldShowPlusPill, remainingCount } =
          getThumbnailsToShow(look.products, product.id);

        return (
          <div
            key={`${look.products[0]?.id}-${index}`}
            className={classnames(styles.carouselItem, {
              singleLook: isSingleLook,
              multipleLooks: !isSingleLook,
            })}
          >
            <div className={styles.heroImage}>
              {renderStylingLookHeroImage(look, index)}
              <LookLabel look={look} />
            </div>
            <div className={styles.thumbnailContainer}>
              {thumbnailsToShow.map((product, index) => (
                <div
                  key={`${product?.id}-${index}`}
                  className={styles.thumbnail}
                >
                  {renderStylingLookThumbnailImage(product, index)}
                </div>
              ))}
              {shouldShowPlusPill && (
                <PlusPill
                  number={remainingCount.toString()}
                  additionalClasses={styles.lightTheme}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
